import { enumToRecord } from '../helpers/object.helpers';
import { snakeCase } from 'lodash';
import { ActivityId } from '../models/activity-class.model';
import { IExpressionVariableConstants } from '../models/expression-variables.model';
import { Coordinate } from '@shared/models/geography.model';

/**
 * Value used by select properties to inherit count from parent
 */
export const ELEMENT_PROPERTY_SELECT_INHERIT_COUNT = 'none';

export const LA_PER_APARTMENT = 60;

/**
 * Stockholm, the capital, is often a good reference point for typical Swedish temperatures. It is located in the Mälaren Valley, which tends to have relatively mild summers and cold winters compared to the rest of the country.
 * In July, daytime highs in Stockholm typically range from 19°C to 24°C (66°F to 75°F), while in January, they range from −9°C to 3°C
 */
export const COORDINATE_STOCKHOLM: Coordinate = {
  lng: 18.064924,
  lat: 59.332581,
};

export const COORDINATE_LUND: Coordinate = {
  lng: 13.196389,
  lat: 55.703611,
};

export const COORDINATE_SKELLEFTEA: Coordinate = {
  lng: 21.32,
  lat: 64.63,
};

export const COORDINATE_UMEA: Coordinate = { lat: 63.83, lng: 20.29 };

export const COORDINATE_HELSINGBORG: Coordinate = {
  lng: 12.701224,
  lat: 56.049909,
};

export const COORDINATE_KRISTIANSTAD: Coordinate = {
  lng: 14.16,
  lat: 56.03,
};

export const COORDINATE_KALMAR: Coordinate = {
  lng: 16.36,
  lat: 56.67,
};

export const COORDINATE_NYC: Coordinate = {
  lng: -74.006,
  lat: 40.7128,
};

export const expressionVariablesConstants: Readonly<IExpressionVariableConstants> =
  {
    gfa_floor_base_per_stairwell_per_level: 15.5,
    gfa_floor_per_apartment: 1.25,
    gfa_functional_spaces_per_gfa_building: 0.05,
    gfa_installations_per_gfa_building: 0.012,
    gfa_other_per_stairwell_per_level: 6.45,
    gfa_per_elevator_per_level: 5.6,
    gfa_stairs_per_stairwell_per_level: 6.5,
    floor_slab_height: 0.28,

    // INNER WALLS
    length_parting_walls_per_stairwell_gfa: 0.74,
    // Half the perimeter-to-area ratio of a `gfa_per_elevator_per_storey` shaft: a square 5.6 m²
    // shaft has a ratio of 1.69, and the other half of its enclosure is already covered by
    // `length_parting_walls_per_stairwell_gfa` (which applies to `gfa_stairwells`, shaft included).
    // DEV-3451 renamed this from `length_parting_walls_per_elevator` without changing the value —
    // the coefficient was always metres per m², only its application to the raw elevator count
    // was wrong. Do not treat the rename as a recalibration.
    length_parting_walls_per_elevator_gfa: 0.84,
    length_apartment_parting_walls_per_sqrt_la_per_apartment: 0.8375534897,
    thickness_apartment_parting_internal_walls: 0.12,
    thickness_stairwell_parting_walls: 0.2,
    thickness_elevator_shaft_walls: 0.25,
    length_non_parting_bearing_internal_walls_per_la_apartments: 0 /* TO DO */,
    length_curtain_walls_per_la_apartments: 0.31,

    // LIVING AREA
    la_apartment_per_gfa_apartment: 0.985,
    gfa_balcony_per_la_apartment: 0.15,
    gfa_balcony_per_apartment: 5,
    la_apartment_per_apartment: LA_PER_APARTMENT,
    bathroom_area_per_apartment: 5,

    // STAIRWELLS
    gfa_per_elevator_per_storey: 5.6,
    elevator_count_per_stairwell: 1,
    gfa_openings_per_stairwell_per_storey: 1,
    gfa_stairs_per_stairwell_per_storey: 6.5,
    gfa_per_apartment_door: 1.25,
    gfa_base_per_stairwell_per_storey: 15.5,
    apartments_per_stairwell_per_storey: 4,
    elevators_per_stairwell: 1,

    // LAUNDRY ROOM
    gfa_laundry_room: 40,

    // BIKE ROOM
    gfa_bike_room: 60,

    // STORAGE
    gfa_storage: 100,

    // FACADES
    entrances_per_perimeter: 0.044,
    gwa_per_entrance: 2.15,
    columns_per_perimeter: 0.22,
    facades_width: 0.4,

    // FLOORS
    floor_span: 5,

    // FOUNDATION
    piling_count_per_building_gfa: 0.036, // from SBEF report

    // OTHER
    utilities_per_gfa: 0.05,
    installations_per_gfa: 0.012,

    activities: Object.fromEntries(
      Object.entries(enumToRecord(ActivityId)).map(([k, v]) => [
        snakeCase(k.toString()).toUpperCase(),
        v,
      ]),
    ),
  };
